import { Controller } from "@hotwired/stimulus";

const EMAIL_REGEXP = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default class extends Controller {
  static targets = ["field", "message", "submit"];
  static values = {
    blankMessage: String,
    invalidMessage: String,
    forbiddenDomainMessage: String,
    forbiddenDomains: Array,
    sameEmailMessage: String,
    equalFieldGroups: Array,
  };

  connect() {
    this.listeners = new Map();

    this.fieldTargets.forEach((field) => {
      const inputHandler = () => this.onInput(field);
      const blurHandler = () => this.onBlur(field);

      this.listeners.set(field, { inputHandler, blurHandler });
      field.addEventListener("input", inputHandler);
      field.addEventListener("blur", blurHandler);
      this.clearValidation(field);
    });

    this.updateSubmitState();
  }

  disconnect() {
    this.listeners?.forEach(({ inputHandler, blurHandler }, field) => {
      field.removeEventListener("input", inputHandler);
      field.removeEventListener("blur", blurHandler);
    });
  }

  onInput(field) {
    if (this.normalizedValue(field.value) !== this.originalValue(field) || this.isTouched(field)) {
      field.dataset.emailValidationTouched = "true";
    }

    this.validateField(field);
    this.validateRelatedFields(field);
  }

  onBlur(field) {
    field.dataset.emailValidationTouched = "true";
    this.validateField(field);
    this.validateRelatedFields(field);
  }

  validateField(field) {
    const value = this.normalizedValue(field.value);
    const originalValue = this.originalValue(field);
    const validateBlankOnTouch = field.dataset.emailValidationValidateBlankOnTouch === "true";

    if (value === originalValue && !(validateBlankOnTouch && this.isTouched(field) && value === "")) {
      this.clearValidation(field);
      return
    }

    const message = this.validationMessage(value, field);
    if (message) {
      this.showValidation(field, message);
    } else {
      this.clearValidation(field);
    }
  }

  validationMessage(value, field) {
    if (value === "") {
      return this.blankMessageValue;
    }

    if (!EMAIL_REGEXP.test(value)) {
      return this.invalidMessageValue;
    }

    const lowerValue = value.toLowerCase();
    const hasForbiddenDomain = this.forbiddenDomainsValue.some((domain) =>
      lowerValue.endsWith(`@${domain.toLowerCase()}`)
    );

    if (hasForbiddenDomain) {
      return this.forbiddenDomainMessageValue;
    }

    return this.equalFieldMessage(value, field);
  }

  equalFieldMessage(value, field) {
    if (!this.hasEqualFieldGroupsValue) return "";

    const fieldName = field.dataset.emailValidationField;
    const equalFieldGroup = this.equalFieldGroupsValue.find(
      ([primaryFieldName]) => primaryFieldName === fieldName
    );

    if (!equalFieldGroup || equalFieldGroup.length < 2) return "";

    const comparedFields = equalFieldGroup
      .slice(1)
      .map((comparedFieldName) => this.fieldByName(comparedFieldName));

    if (comparedFields.some((comparedField) => !comparedField)) return "";

    const comparedValues = comparedFields.map((comparedField) =>
      this.normalizedValue(comparedField.value)
    );

    if (comparedValues.some((comparedValue) => comparedValue === "" || !EMAIL_REGEXP.test(comparedValue))) {
      return "";
    }

    return comparedValues.includes(value) ? this.sameEmailMessageValue : "";
  }

  showValidation(field, message) {
    const messageElement = this.messageElement(field);

    field.classList.add("is-invalid");
    field.setAttribute("aria-invalid", "true");
    field.setCustomValidity(message);

    if (messageElement) {
      messageElement.textContent = message;
      messageElement.classList.remove("d-none");
      messageElement.classList.add("d-block");
    }

    this.updateSubmitState();
  }

  clearValidation(field) {
    const messageElement = this.messageElement(field);

    field.classList.remove("is-invalid");
    field.removeAttribute("aria-invalid");
    field.setCustomValidity("");

    if (messageElement) {
      messageElement.textContent = "";
      messageElement.classList.add("d-none");
      messageElement.classList.remove("d-block");
    }

    this.updateSubmitState();
  }

  updateSubmitState() {
    if (!this.hasSubmitTarget) return;

    const hasValidationError = this.fieldTargets.some((field) =>
      this.fieldBlocksSubmit(field)
    );

    this.submitTargets.forEach((submit) => {
      submit.disabled = hasValidationError;
      submit.toggleAttribute("disabled", hasValidationError);
    });
  }

  fieldBlocksSubmit(field) {
    if (field.classList.contains("is-invalid")) return true;

    const validateBlankOnTouch = field.dataset.emailValidationValidateBlankOnTouch === "true";
    return validateBlankOnTouch && this.normalizedValue(field.value) === "";
  }

  messageElement(field) {
    return this.messageTargets.find(
      (message) => message.dataset.emailValidationField === field.dataset.emailValidationField
    );
  }

  validateRelatedFields(field) {
    if (!this.hasEqualFieldGroupsValue) return;

    const fieldName = field.dataset.emailValidationField;
    this.equalFieldGroupsValue
      .filter((equalFieldGroup) => equalFieldGroup.includes(fieldName))
      .map(([primaryFieldName]) => this.fieldByName(primaryFieldName))
      .filter((relatedField) => relatedField && relatedField !== field)
      .forEach((relatedField) => this.validateField(relatedField));
  }

  fieldByName(fieldName) {
    return this.fieldTargets.find(
      (field) => field.dataset.emailValidationField === fieldName
    );
  }

  normalizedValue(value) {
    return (value || "").trim();
  }

  originalValue(field) {
    return this.normalizedValue(field.dataset.emailValidationOriginalValue);
  }

  isTouched(field) {
    return field.dataset.emailValidationTouched === "true";
  }
}
